import { create } from 'zustand'
import type { ConfigNode } from '../types/config.types'
import { configService } from '../services/configService'

export function findNodeById(nodes: ConfigNode[], nodeId: string): ConfigNode | null {
  for (const n of nodes) {
    if (n.id === nodeId) return n
    if (n.children?.length) {
      const found = findNodeById(n.children, nodeId)
      if (found) return found
    }
  }
  return null
}

function isProtectedBaseNode(node: ConfigNode): boolean {
  if (node.isBase !== true) return false
  // User-created object sub-branches may be marked base-like in old saved trees;
  // they must remain deletable per product rule.
  if (node.type === 'objectAttributes' || node.type === 'objectTabularSections' || node.type === 'objectForms' || node.type === 'objectLayout') {
    return false
  }
  return true
}

export function cloneTreeAndAddChild(nodes: ConfigNode[], parentId: string, newNode: ConfigNode): ConfigNode[] {
  return nodes.map((node) => {
    if (node.id !== parentId) {
      return {
        ...node,
        children: node.children?.length ? cloneTreeAndAddChild(node.children, parentId, newNode) : undefined,
      }
    }
    const children = [...(node.children ?? []), newNode]
    return { ...node, children }
  })
}

export function cloneTreeAndRemoveNode(nodes: ConfigNode[], nodeId: string): ConfigNode[] {
  return nodes.map((node) => {
    if (node.children?.length) {
      const filtered = node.children.filter((c) => c.id !== nodeId)
      if (filtered.length !== node.children.length) {
        return { ...node, children: filtered.length ? filtered : undefined }
      }
      return {
        ...node,
        children: cloneTreeAndRemoveNode(node.children, nodeId),
      }
    }
    return node
  })
}

function cloneTreeAndUpdateNodeName(nodes: ConfigNode[], nodeId: string, name: string): ConfigNode[] {
  return nodes.map((node) => {
    if (node.id === nodeId) return { ...node, name }
    if (node.children?.length) {
      return { ...node, children: cloneTreeAndUpdateNodeName(node.children, nodeId, name) }
    }
    return node
  })
}

function cloneTreeAndUpdateNodeAttribute(
  nodes: ConfigNode[],
  nodeId: string,
  attribute: ConfigNode['attribute']
): ConfigNode[] {
  return nodes.map((node) => {
    if (node.id === nodeId) {
      if (node.type !== 'objectAttribute' && node.type !== 'commonAttribute') {
        return node
      }
      const next: ConfigNode = { ...node, attribute: attribute ? { ...attribute } : undefined }
      if (!attribute) delete next.attribute
      return next
    }
    if (node.children?.length) {
      return { ...node, children: cloneTreeAndUpdateNodeAttribute(node.children, nodeId, attribute) }
    }
    return node
  })
}

export function cloneTreeAndPatchNode(
  nodes: ConfigNode[],
  nodeId: string,
  patch: Partial<ConfigNode>
): ConfigNode[] {
  return nodes.map((node) => {
    if (node.id === nodeId) return { ...node, ...patch }
    if (node.children?.length) {
      return { ...node, children: cloneTreeAndPatchNode(node.children, nodeId, patch) }
    }
    return node
  })
}

interface ConfigState {
  tree: ConfigNode[]
  selectedIds: string[]
  expandedIds: string[]
  searchQuery: string
  isLoading: boolean
  error: string | null
  /** Tree has unsaved DB changes (use Save configuration on the toolbar) */
  configurationDirty: boolean

  setTree: (tree: ConfigNode[]) => void
  setSelectedIds: (ids: string[]) => void
  setExpandedIds: (ids: string[]) => void
  toggleExpanded: (id: string) => void
  setSearchQuery: (query: string) => void
  /** Pass selected connection id (or null) so tree loads from that DB or falls back to base mock */
  loadTree: (connectionId: string | null) => Promise<void>
  clearError: () => void
  /** Add child under parentId. Expands branch and selects the new node. */
  addChildNode: (parentId: string, newNode: ConfigNode) => void
  /** Remove node (user-added only, not base). */
  removeNode: (nodeId: string) => void
  /** Update node display name (sync with properties panel). */
  updateNodeName: (nodeId: string, name: string) => void
  /** Persist attribute metadata on objectAttribute / commonAttribute for DB sync and runtime forms. */
  updateNodeAttribute: (nodeId: string, attribute: ConfigNode['attribute']) => void
  /** Patch extra node fields persisted in designer tree. */
  updateNodePatch: (nodeId: string, patch: Partial<ConfigNode>) => void
  /** Mark the in-memory tree as matching the last saved configuration. */
  markConfigurationClean: () => void
}

export const useConfigStore = create<ConfigState>((set, get) => ({
  tree: [],
  selectedIds: [],
  expandedIds: ['root', 'common', 'catalogs', 'documents'],
  searchQuery: '',
  isLoading: false,
  error: null,
  configurationDirty: false,

  setTree: (tree) => set({ tree, configurationDirty: true }),
  setSelectedIds: (selectedIds) => set({ selectedIds }),
  setExpandedIds: (expandedIds) => set({ expandedIds }),
  toggleExpanded: (id) => {
    const { expandedIds } = get()
    set({
      expandedIds: expandedIds.includes(id)
        ? expandedIds.filter((x) => x !== id)
        : [...expandedIds, id],
    })
  },
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  loadTree: async (connectionId: string | null) => {
    set({ isLoading: true, error: null })
    try {
      const tree = await configService.getTree(connectionId)
      set({ tree, isLoading: false, configurationDirty: false })
    } catch (e) {
      set({
        isLoading: false,
        error: e instanceof Error ? e.message : 'Failed to load config',
      })
    }
  },
  clearError: () => set({ error: null }),
  addChildNode: (parentId, newNode) => {
    const { tree, expandedIds } = get()
    const newTree = cloneTreeAndAddChild(tree, parentId, newNode)
    set({
      tree: newTree,
      expandedIds: expandedIds.includes(parentId) ? expandedIds : [...expandedIds, parentId],
      selectedIds: [newNode.id],
      configurationDirty: true,
    })
  },
  removeNode: (nodeId) => {
    const { tree, selectedIds } = get()
    const node = findNodeById(tree, nodeId)
    if (node && isProtectedBaseNode(node)) return
    const newTree = cloneTreeAndRemoveNode(tree, nodeId)
    set({
      tree: newTree,
      selectedIds: selectedIds.includes(nodeId) ? [] : selectedIds,
      configurationDirty: true,
    })
  },
  updateNodeName: (nodeId, name) => {
    const { tree } = get()
    set({ tree: cloneTreeAndUpdateNodeName(tree, nodeId, name), configurationDirty: true })
  },
  updateNodeAttribute: (nodeId, attribute) => {
    const { tree } = get()
    set({ tree: cloneTreeAndUpdateNodeAttribute(tree, nodeId, attribute), configurationDirty: true })
  },
  updateNodePatch: (nodeId, patch) => {
    const { tree } = get()
    set({ tree: cloneTreeAndPatchNode(tree, nodeId, patch), configurationDirty: true })
  },
  markConfigurationClean: () => set({ configurationDirty: false }),
}))
